import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Star as StarIcon, EmojiEvents as TrophyIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Confetti, Particles, Sparkle, GlowBorder, ProfileEffectOverlay } from '../effects';

const CONGRATULATION_MESSAGES = [
  'Hienoa työtä!',
  'Mahtavaa!',
  'Loistavaa!',
  'Huippua!',
  'Täydellistä!',
  'Wau!',
  'Olet mestari!',
  'Superhienoa!',
  'Jes!',
  'Aivan mahtavaa!',
  'Olet tähti!',
  'Hyvin tehty!',
  'Upea suoritus!',
  'Nappiin meni!',
  'Sä oot paras!',
  'Huikea homma!',
  'Tosi hyvä!',
  'Kymppi!',
  'Olet sankari!',
  'Fantastista!',
  'Priimaa!',
  'Erinomaista!',
  'Sulla menee hyvin!',
  'Jatka samaan malliin!',
  'Olet ahkera!',
  'Hyvä sinä!',
  'Näin se tehdään!',
  'Oikein hyvä!',
  'Sä osaat!',
  'Taitavaa!',
  'Upeaa työtä!',
  'Olet superapuri!',
];

const TaskCompletionAnimation = ({ task, onComplete, accentColor = '#e91e8c', showCoins = false, pendingApproval = false, theme, userEffect, loop = false }) => {
  const [effectsActive, setEffectsActive] = useState(true);
  const [effectData, setEffectData] = useState(null);
  const [loopKey, setLoopKey] = useState(0);

  const message = useMemo(() =>
    pendingApproval
      ? 'Tehty!'
      : CONGRATULATION_MESSAGES[Math.floor(Math.random() * CONGRATULATION_MESSAGES.length)],
  [pendingApproval]);

  const isPremium = theme?.isSuper || !!userEffect;
  // User's equipped effect overrides theme's effect
  const taskCompleteEffect = userEffect ? 'profileEffect' : theme?.animations?.taskComplete;
  const effectSrc = userEffect
    ? `/uploads/profile-effects/${userEffect}/effect.json`
    : theme?.animations?.profileEffect?.src;
  const glowColor = theme?.effects?.glowColor || theme?.main || accentColor;
  const particleColors = [theme?.main, theme?.light, theme?.dark].filter(Boolean);

  // Handle effect completion - restart if looping
  const handleEffectComplete = () => {
    if (loop) {
      setLoopKey(k => k + 1);
      setEffectsActive(true);
    } else {
      setEffectsActive(false);
    }
  };

  // Fetch profile effect JSON when needed
  useEffect(() => {
    if (taskCompleteEffect === 'profileEffect' && effectSrc) {
      fetch(effectSrc)
        .then(r => r.json())
        .then(setEffectData)
        .catch(err => {
          console.error('Failed to load profile effect:', err);
          setEffectsActive(false);
        });
    }
  }, [taskCompleteEffect, effectSrc]);

  const renderPremiumEffects = () => {
    if (!isPremium || !effectsActive) return null;

    switch (taskCompleteEffect) {
      case 'confetti':
        return (
          <Confetti
            key={loopKey}
            trigger
            count={60}
            colors={particleColors.length ? particleColors : undefined}
            duration={2500}
            onComplete={handleEffectComplete}
          />
        );
      case 'sparkle':
        return (
          <Sparkle
            key={loopKey}
            trigger
            color={theme?.main || accentColor}
            count={12}
            size={28}
            spread={80}
            duration={1000}
            onComplete={handleEffectComplete}
          />
        );
      case 'bounce':
        return (
          <Particles
            key={loopKey}
            trigger
            type="stars"
            count={15}
            duration={1800}
            direction="up"
            onComplete={handleEffectComplete}
          />
        );
      case 'particles':
        return (
          <Particles
            key={loopKey}
            trigger
            type="sparkles"
            count={25}
            duration={2000}
            spread={120}
            direction="up"
            onComplete={handleEffectComplete}
          />
        );
      case 'fireworks':
        return (
          <>
            <Confetti key={`c-${loopKey}`} trigger count={40} colors={particleColors.length ? particleColors : undefined} duration={2000} />
            <Sparkle key={`s-${loopKey}`} trigger color={theme?.main} count={10} spread={100} duration={1200} onComplete={handleEffectComplete} />
          </>
        );
      case 'profileEffect': {
        const pe = theme?.animations?.profileEffect;
        return effectData ? (
          <ProfileEffectOverlay
            key={loopKey}
            effect={effectData}
            width={pe?.width || 450}
            height={pe?.height || 880}
            scale={pe?.scaleToCard ? 0.5 : 1}
            onComplete={handleEffectComplete}
            zIndex={1000}
          />
        ) : null;
      }
      default:
        return (
          <Particles
            key={loopKey}
            trigger
            type="coins"
            count={12}
            duration={1500}
            onComplete={handleEffectComplete}
          />
        );
    }
  };

  const cardContent = (
    <Box
      sx={{
        bgcolor: 'rgba(255,255,255,0.92)',
        borderRadius: '20px',
        p: { xs: 2, sm: 3 },
        textAlign: 'center',
        boxShadow: `0 8px 32px ${accentColor}55`,
        border: `3px solid ${accentColor}`,
        minWidth: 180,
        maxWidth: { xs: 200, sm: 220 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <motion.div
        animate={{ scale: [1, 1.35, 1] }}
        transition={{ duration: 0.65, repeat: 3, ease: 'easeInOut' }}
      >
        {task?.icon ? (
          <Typography sx={{ fontSize: { xs: 40, sm: 52 }, mb: 0.5, display: 'block' }}>{task.icon}</Typography>
        ) : (
          <TrophyIcon sx={{ fontSize: { xs: 40, sm: 52 }, color: accentColor, mb: 0.5 }} />
        )}
      </motion.div>

      <Typography variant="h6" fontWeight={900} sx={{ color: '#1a1a2e', mb: 0.25, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
        {message}
      </Typography>

      <Typography variant="body2" fontWeight={700} sx={{ color: '#1a1a2e', mb: 1.5 }}>
        {task?.title}
      </Typography>

      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          bgcolor: pendingApproval ? '#ff9800' : showCoins ? '#ffc107' : accentColor,
          borderRadius: 50,
          px: 1.5,
          py: 0.25,
        }}
      >
        {pendingApproval ? (
          <>
            <Typography sx={{ fontSize: '1rem' }}>⏳</Typography>
            <Typography variant="body2" fontWeight={900} sx={{ color: '#fff' }}>
              Odottaa hyväksyntää
            </Typography>
          </>
        ) : showCoins ? (
          <>
            <Typography sx={{ fontSize: '1rem' }}>🪙</Typography>
            <Typography variant="body2" fontWeight={900} sx={{ color: '#000' }}>
              +{task?.reward_points || 0} kolikkoa!
            </Typography>
          </>
        ) : (
          <>
            <StarIcon sx={{ color: '#fff', fontSize: 16 }} />
            <Typography variant="body2" fontWeight={900} sx={{ color: '#fff' }}>
              +{task?.reward_points || 0} pistettä!
            </Typography>
          </>
        )}
      </Box>

      <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'text.secondary', fontSize: '0.7rem' }}>
        Napauta sulkeaksesi
      </Typography>
    </Box>
  );

  const isProfileEffect = taskCompleteEffect === 'profileEffect';

  return (
    <motion.div
      onClick={onComplete}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isProfileEffect ? 'rgba(0,0,0,0.4)' : `${accentColor}cc`,
        zIndex: 10,
        cursor: 'pointer',
      }}
    >
      {/* Profile effect renders behind the card, filling the whole area */}
      {isProfileEffect && effectData && (
        <ProfileEffectOverlay
          key={loopKey}
          effect={effectData}
          fillContainer
          onComplete={handleEffectComplete}
          zIndex={1}
        />
      )}

      {/* Other effects render on top */}
      {!isProfileEffect && renderPremiumEffects()}

      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0, opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        style={{ zIndex: 2, position: 'relative' }}
      >
        {isPremium && theme?.animations?.completionGlow ? (
          <GlowBorder color={glowColor} intensity={0.8} pulseSpeed={1.5} borderRadius={24}>
            {cardContent}
          </GlowBorder>
        ) : (
          cardContent
        )}
      </motion.div>
    </motion.div>
  );
};

export default TaskCompletionAnimation;
